import * as React from 'react';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { ISharingWizardProps, ISharingInformation } from '../../controls/shareActionControl';
import { injectInitialProps } from '../../utilities/components/Props';
import { CancelError } from '../../utilities/async/CancelError';
import * as SharingCalloutStyles from './SharingCallout.scss';
import { IWithWizardInjectedProps } from '../decorators/withWizard';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { displayNameFacet } from '../../facets/ItemFacets';
import { ITheme } from 'office-ui-fabric-react/lib/Styling';
import { IPropsWithStyles, styled } from 'office-ui-fabric-react/lib/Utilities';
import { IActionStateProps, WithAction } from '../withAction/WithAction';
import { sharingCalloutControl, IInitializeShareFrame } from '../../controls/sharingCalloutControl';
import { localeFacet, configurationItemKey } from '../../facets/ConfigurationFacets';
import { findElementRecursive } from '@uifabric/utilities/lib/dom/findElementRecursive';
import DialogStrings from './ShareDialog.resx';
import { ItemManageAccessAction } from '../actions/manageAccess/ManageAccessAction';

const COPY_LINK_MODE = 2;
const COPY_LINK_STRING = '&mode=copy';
const ADD_PEOPLE_MODE = 7;
const ADD_PEOPLE_STRING = '&mode=addPeople';

export interface ISharingCalloutState {
  sharingInformation?: ISharingInformation;
  isSharingDialogLoaded: boolean;
  didSharingHappen: boolean;
  iframeSource: string;
  isOpen: boolean;
  iframeHeight?: number;
  error: Error | undefined;
}

const INITIAL_STATE: ISharingCalloutState = {
  sharingInformation: undefined,
  isSharingDialogLoaded: false,
  didSharingHappen: false,
  iframeSource: '#',
  isOpen: true,
  iframeHeight: 0,
  error: undefined
};

// Callout that contains an iframed sharing dialog integrated using this documentation:
// https://onedrive.visualstudio.com/OneDriveWeb/
// _git/odsp-common?path=%2Fodsp-shared-sharing%2Fdocs%2Fintegration.md&version=GBmaster&_a=preview
class SharingCalloutInner extends React.PureComponent<ISharingCalloutInnerProps, ISharingCalloutState> {
  private _sharePrefetchRef: React.RefObject<HTMLDivElement>;
  constructor(props: ISharingCalloutBaseProps) {
    super(props);
    this.state = INITIAL_STATE;
    this._sharePrefetchRef = React.createRef();
  }

  public componentWillMount(): void {
    this._getSharingInformation();
  }

  public componentDidMount(): void {
    window.addEventListener('message', this._listenForMessages, false);
    this._update(INITIAL_STATE);
  }

  public componentWillUnmount(): void {
    window.removeEventListener('message', this._listenForMessages, false);
  }

  public componentDidUpdate(previousProps: ISharingCalloutInnerProps, previousState: ISharingCalloutState): void {
    this._update(previousState);
  }

  public render(): JSX.Element | null {
    const {
      isSharingDialogLoaded,
      iframeSource,
      isOpen,
      iframeHeight,
      error
    }: ISharingCalloutState = this.state;

    const classicIframeElement = iframeSource === '#' ? null : (
      <iframe
        id='shareFrame'
        name='shareFrame'
        src={ iframeSource }
        style={ {
          visibility: isSharingDialogLoaded ? 'visible' : 'hidden',
          width: isSharingDialogLoaded ? '100%' : '0',
          height: iframeHeight + 'px'
        } }
      />
    );

    const calloutInitialHeight = this.props.mode === COPY_LINK_MODE ? 150 : 402;
    const calloutHeight = iframeHeight ? iframeHeight : calloutInitialHeight;
    const alignItems = !isSharingDialogLoaded ? 'center' : 'flex-start';
    if (isOpen) {
      return (
        <Callout
          onDismiss={ this._onClose }
          target={ this.props.calloutTarget }
          preventDismissOnScroll={ true }
          isBeakVisible={ false }
          hideOverflow={ true }
        >
          <div
            className={ `${SharingCalloutStyles.sharingContents}` }
            ref={ this._sharePrefetchRef }
            style={ { minHeight: `${calloutHeight}px`, alignItems: alignItems } }
          >
            { !isSharingDialogLoaded && !error &&
              (<Spinner size={ SpinnerSize.large } />)
            }
            { classicIframeElement }
          </div>
        </Callout>
      );
    } else {
      return null;
    }
  }

  private _listenForMessages = (sharingEvent: MessageEvent): void => {
    let sharingEventData;
    try {
      sharingEventData = JSON.parse(sharingEvent.data);
    } catch (e) {
      return;
    }

    // Determine what the message is and respond appropriately.
    const sharingEventName = sharingEventData.name;
    if (sharingEventName === 'share_resize' && sharingEventData.height) {
      this.setState({ iframeHeight: sharingEventData.height });
      // Bug in POST messages means sharingEventData.width comes back as zero.
      // Using min-width 320px instead.
    } else if (sharingEventName === 'share_ready') {
      this.setState({ isSharingDialogLoaded: true });
    } else if (sharingEventName === 'share_error') {
      if (sharingEventData.error) {
        this.setState({
          error: sharingEventData.error
        });
      } else {
        this.setState({
          error: new Error(DialogStrings.sharedialogErrorMessage)
        });
      }
    } else if (sharingEventName === 'share_completed') {
      this.setState({ didSharingHappen: true });
    } else if (sharingEventName === 'share_dismiss') {
      this._onClose();
    } else if (sharingEventName === 'share_manageAccessClicked') {
      const {
        openManageAccess
      } = this.props;

      if (openManageAccess) {
        openManageAccess();
      }
    }
  }

  private _onClose = (): void => {
    const { error } = this.state;
    // Check if the user shared the item or canceled the dialog without sharing
    if (this.state.didSharingHappen) {
      this.props.resolve();
    } else if (error) {
      this.props.reject(error);
    } else {
      this.props.reject(new CancelError());
    }
    this.setState({
      isOpen: false,
      error: undefined
    });
  }

  private _getSharingInformation = async (): Promise<void> => {
    try {
      const sharingInformation = await this.props.getSharingInformation();

      this.setState({
        sharingInformation: sharingInformation
      });
    } catch (e) {
      this.props.reject(e);
    }
  }

  private _update = (previousState: ISharingCalloutState): void => {
    const {
      sharingInformation
    } = this.state;

    if (sharingInformation && !previousState.sharingInformation) {
      const {
        authToken,
        webAbsoluteUrl,
        serverRelativeItemUrl,
        listUrl,
        cookieAuthIframeUrl
      } = sharingInformation;
      const {
        initializeShareFrame,
        defaultRecipients,
        openManageAccess
      } = this.props;

      let modeValue;
      switch (this.props.mode) {
        case (COPY_LINK_MODE):
          modeValue = COPY_LINK_STRING;
          break;
        case (ADD_PEOPLE_MODE):
          modeValue = ADD_PEOPLE_STRING;
          break;
        default:
          modeValue = '';
      }

      /**
       * We use a different URL for the iframe depending on whether
       * we are using token based authentication or cookie based
       * authentication.
       */
      if (authToken && webAbsoluteUrl && serverRelativeItemUrl && listUrl && initializeShareFrame) {
        const calloutProps = this.props;
        const parentElement = this._sharePrefetchRef.current as HTMLElement;
        initializeShareFrame({ parentElement, sharingInformation, calloutProps });
      } else if (cookieAuthIframeUrl) {
        this.setState({
          iframeSource: `${cookieAuthIframeUrl}${
            modeValue}${defaultRecipients && defaultRecipients.length > 0 ?
              `&defaultRecipients=${encodeURI(defaultRecipients.join(','))}` :
              ''}&ma=${openManageAccess ? 1 : 0}`
        });
      } else {
        this.props.reject(new Error('Missing sharing information'));
      }
    }
  }
}

export const StyledSharingCalloutInner = styled(SharingCalloutInner, (props: ISharingCalloutActionProps) => ({}));
export interface ISharingCalloutBaseProps extends ISharingWizardProps {
  locale: string;
  itemName: string;
  calloutTarget?: HTMLElement;
  openManageAccess?: () => void;
  initializeShareFrame: ((authTokenProps: IInitializeShareFrame) => void) | undefined;
}

export type ISharingCalloutInnerProps = ISharingCalloutBaseProps & IPropsWithStyles<{}, {}> & {
  theme?: ITheme;
};

export type ISharingCalloutActionProps = ISharingCalloutBaseProps & {
  sharingCallout: React.ComponentType<ISharingCalloutBaseProps>;
};

class SharingCalloutAction extends React.PureComponent<ISharingCalloutActionProps> {
  public render(): JSX.Element | null {
    const {
      sharingCallout: FinalSharingCallout,
      ...props
    } = this.props;

    return (
      <WithAction action={ <ItemManageAccessAction itemKey={ props.itemKey } /> }>
        { ({ isAvailable, execute }: IActionStateProps) => {
          const openManageAccessAction = isAvailable ? () => {
            execute({});
          } : undefined;

          return (
            <FinalSharingCallout
              openManageAccess={ openManageAccessAction }
              { ...props }
            />
          );
        } }
      </WithAction>
    );
  }
}

const WithCalloutTarget = injectInitialProps((props: Pick<IWithWizardInjectedProps, 'onExecuteProps'> & Pick<ISharingWizardProps, 'mode'>):
  Pick<ISharingCalloutBaseProps, 'calloutTarget'> => {
  const {
    onExecuteProps
  } = props;

  const onExecutePropsEvent = onExecuteProps && onExecuteProps.event;
  let targetElement = (onExecutePropsEvent) ? onExecutePropsEvent.target as HTMLElement : undefined;
  const commandQueryString = (commandName: string) => {
    return `.ms-Button--commandBar[data-automationId="${commandName}"]`;
  };
  const queryShareCommands = document.querySelectorAll(commandQueryString('shareCommand'));
  const queryCopyCommands = document.querySelectorAll(commandQueryString('copyLinkCommand'));
  const queryAddPeopleCommands = document.querySelectorAll(commandQueryString('addPeopleCommand'));
  let shareCommand = queryShareCommands[0] as HTMLElement;
  let copyCommand = queryCopyCommands[0] as HTMLElement;
  let addPeopleCommand = queryAddPeopleCommands[0] as HTMLElement;
  /**
   * Click share/copy command from command bar
   * and Pressing key S will result an extra invisible command,
   * so we want to select the later one, which is visible.
   */
  if (queryShareCommands.length === 2) {
    shareCommand = queryShareCommands[1] as HTMLElement;
    copyCommand = queryCopyCommands[1] as HTMLElement;
    addPeopleCommand = queryAddPeopleCommands[1] as HTMLElement;
  }

  let targetCommandElement: HTMLElement | undefined | null;
  switch (props.mode) {
    case (1):
      targetCommandElement = shareCommand;
      break;
    case (2):
      targetCommandElement = copyCommand;
      break;
    case (7):
      targetCommandElement = addPeopleCommand;
      break;
  }

  if (!targetCommandElement) {
    targetCommandElement = targetElement && findElementRecursive(targetElement, (element: HTMLElement) => element.tagName === 'BUTTON');
  }

  return {
    calloutTarget: targetCommandElement || targetElement
  };

})(SharingCalloutAction);

export const SharingCallout: React.ComponentType<ISharingWizardProps> = connectToItemCache((
  itemCache: IItemCache,
  props: Pick<ISharingWizardProps, 'itemKey'>
): Pick<ISharingCalloutBaseProps, 'itemName' | 'locale' | 'initializeShareFrame'>
  & Pick<ISharingCalloutActionProps, 'sharingCallout'> => {
  const { itemKey } = props;
  const {
    displayName
  } = itemCache.demandItem({
    displayName: displayNameFacet,
  }, itemKey);
  const locale = itemCache.demandItemFacet(localeFacet, configurationItemKey);
  const controlHandler = itemCache.demandItemFacet(sharingCalloutControl, itemKey);

  const {
    initializeShareFrame
  } = sharingCalloutControl.evaluate(controlHandler)(itemCache, {
    itemKey
  });

  return {
    itemName: displayName || '',
    locale: locale && locale.language || 'en-US',
    sharingCallout: StyledSharingCalloutInner,
    initializeShareFrame: initializeShareFrame
  };
})(WithCalloutTarget);
